import { digger } from '@cbre/digger';
import { concatValuesBy, isDash, simpleValueValidation, defaultTxt, formatNumber } from '@miq/utils';
import { ReportRecord } from '../../../../models/report.model';
import { TemplateFieldsDisplayFn, DisplayFnArgumentsInfo } from '../../../../models/template.model';
import { sortAttributesFields } from '../../utils/argument-sorting';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';

function mapUsageArgumentToPrimary(arg: DisplayFnArgumentsInfo): DisplayFnArgumentsInfo {
  let mappedArgumentObj = arg;
  if (arg.field != null && arg.field.includes('usages')) {
    const field = arg.field.split('usages/').join(`usages[primary_usage_f=true].`);
    mappedArgumentObj = { ...mappedArgumentObj, field };
  }
  return mappedArgumentObj;
}

function getQuarter(quarterDate: Date): number {
  return Math.floor(quarterDate.getMonth() / 3 + 1);
}

function getCompanyByRole(record: ReportRecord, companyRole: string): any {
  const roleField = `usages.company_contact_role_addresses[role_desc='${companyRole}'].party_organization_display_name`;
  return digger(record.data, roleField);
}

function getLatestDevelopmentDate(record: ReportRecord): string {
  const startDateDigger = digger(record.data, 'property_development/development_start_date');
  const completionDates = startDateDigger?.map(stringDate => {
    const date = new Date(stringDate);
    return { date: date, stringDate: stringDate };
  });
  if (Array.isArray(completionDates) && completionDates.length > 0) {
    const dateDates = completionDates.map(datesObj => datesObj.date);
    const latestDate = new Date(Math.max(...dateDates));
    const latestCompletionDate = completionDates.find(
      completionDate => completionDate.date.getTime() - latestDate.getTime() === 0
    );
    return latestCompletionDate?.stringDate;
  } else {
    return completionDates;
  }
}

function padTo2Digits(num: number): string {
  return num.toString().padStart(2, '0');
}

function getFieldByDate(developmentDate: string, field: string, record: ReportRecord) {
  if (developmentDate === null) {
    return defaultTxt;
  }
  const diggerField = `property_development[development_start_date='${developmentDate}'].${field}`;
  const diggerVal = digger(record.data, diggerField);
  return diggerVal;
}

const PROPERTY_COMPANY_ROLES = {
  developer: 'Developer',
  funder: 'Funder',
  architect: 'Architect',
};

const invalidDevelopmentDate = date => date == null || (!isNaN(+date) && +date == 0);

export const displayFnMap: TemplateFieldsDisplayFn = {
  629: {
    // 629 - Address (850, 851, 854)
    displayFn: (record: ReportRecord, options, ...args) => {
      const { displayFnArgumentsOrder } = options;
      const finalArgs = sortAttributesFields(args, displayFnArgumentsOrder);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      const res = concatValuesBy(
        '\n',
        ...values.filter(value => simpleValueValidation(value) && !isDash(value as string))
      );
      return res;
    },
  },
  631: {
    // 631 - Market
    displayFn: (record: ReportRecord, _options, marketArgs, submarketArgs, districtArgs, neighborhoodArgs) => {
      const market = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(marketArgs));
      const submarket = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(submarketArgs));
      const district = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(districtArgs));
      const neighborhood = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(neighborhoodArgs));
      if (simpleValueValidation(neighborhood) && !isDash(neighborhood as string)) {
        return neighborhood;
      }
      if (simpleValueValidation(district) && !isDash(district as string)) {
        return district;
      }
      if (simpleValueValidation(submarket) && !isDash(submarket as string)) {
        return submarket;
      }
      return market;
    },
  },
  632: {
    // 632 - Market Group (863, 862, 861)
    displayFn: (record: ReportRecord, _options, marketArgs, submarketArgs, districtArgs) => {
      const market = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(marketArgs));
      const submarket = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(submarketArgs));
      const district = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(districtArgs));
      const neighborhood = digger(record.data, 'usages[primary_usage_f=true].lig_geography.neighborhood_desc');

      const hasNeighborhoodData = simpleValueValidation(neighborhood) && !isDash(neighborhood as string);
      const hasDistrictData = simpleValueValidation(district) && !isDash(district as string);
      const hasSubmarketData = simpleValueValidation(submarket) && !isDash(submarket as string);

      if (hasNeighborhoodData && hasDistrictData) {
        return district;
      }
      if (hasDistrictData && hasSubmarketData) {
        return submarket;
      }
      return market;
    },
  },
  630: {
    // 630 - Postcode (856)
    displayFn: displayFnWrapper,
  },
  648: {
    // 648 - Latitude (3846)
    displayFn: displayFnWrapper,
  },
  649: {
    // 649 - Longitude (3847)
    displayFn: displayFnWrapper,
  },
  651: {
    // 651 - [Development] Planning Authority (17087)
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'property_development_authority_desc';
      const fieldVal = getFieldByDate(latestDate, field, record);
      const planningAuthority = fieldVal ?? defaultTxt;
      return Array.isArray(planningAuthority) ? concatValuesBy(', ', planningAuthority) : planningAuthority; //
    },
  },
  652: {
    // 652 - [Development] Development Type (17088)
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'development_type_desc';
      const fieldVal = getFieldByDate(latestDate, field, record);
      const devTypeVal = fieldVal ?? defaultTxt;
      return Array.isArray(devTypeVal) ? concatValuesBy(', ', devTypeVal) : devTypeVal; //
    },
  },
  653: {
    // 653 - Total Office Size
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'total_property_development_area';
      const totalOfficeSpace = getFieldByDate(latestDate, field, record);
      const officeSpaceVal = totalOfficeSpace ?? defaultTxt;
      if (Array.isArray(officeSpaceVal)) {
        const value = officeSpaceVal.map(space => formatNumber(space, undefined, '.0-2'));
        return concatValuesBy(', ', ...value);
      }
      return officeSpaceVal;
    },
  },
  654: {
    // 654 - Speculative Office Space
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = `speculative_property_development_area`;
      const specOfficeSpace = getFieldByDate(latestDate, field, record);
      const specOfficeSpaceVal = specOfficeSpace ?? defaultTxt;
      if (Array.isArray(specOfficeSpace)) {
        const mappedSpecSpace = specOfficeSpace.map(specSpace => formatNumber(specSpace, undefined, '.0-2'));
        return concatValuesBy(', ', ...mappedSpecSpace);
      }
      return specOfficeSpaceVal;
    },
  },
  655: {
    // 655 - Committed Office Space
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'commited_property_development_area';
      const committedOfficeSpace = getFieldByDate(latestDate, field, record);
      const commitedSpaceVal = committedOfficeSpace ?? defaultTxt;
      if (Array.isArray(commitedSpaceVal)) {
        const mappedSpaceVal = commitedSpaceVal.map(commitedSpace => formatNumber(commitedSpace, undefined, '.0-2'));
        return concatValuesBy(', ', ...mappedSpaceVal);
      }
      return commitedSpaceVal;
    },
  },
  650: {
    // 650 - [Development] Net Addition (17091, UOM: 17092)
    displayFn: (record: ReportRecord, _options, _args) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'actual_property_development_area';
      const netAdditionVal = getFieldByDate(latestDate, field, record);
      const uomField = 'actual_property_development_area_uom';
      const uomVal = getFieldByDate(latestDate, uomField, record);
      const netAdditionValues = netAdditionVal ?? defaultTxt;
      const uomValues = uomVal ?? ' ';
      if (Array.isArray(netAdditionValues)) {
        const mappedNetAddition = netAdditionValues.map(
          (value, index) => `${formatNumber(value, undefined, '.0-2')} ${uomVal[index] ?? defaultTxt}`
        );
        return concatValuesBy(', ', ...mappedNetAddition);
      }
      const returnVal = `${netAdditionValues} ${uomValues}`;
      return returnVal;
    },
  },
  656: {
    // 656 - [Development] Planning Status (17112)
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'stages.property_development_stage_type_desc';
      const fieldVal = getFieldByDate(latestDate, field, record);
      const planningStatusVal = fieldVal ?? defaultTxt;
      return Array.isArray(planningStatusVal) ? concatValuesBy(' - ', ...planningStatusVal) : planningStatusVal;
    },
  },
  657: {
    // 657 - [Development] Start Date (17093)
    displayFn: (record: ReportRecord, _) => {
      const startDateString = getLatestDevelopmentDate(record);
      if (invalidDevelopmentDate(startDateString)) {
        return defaultTxt;
      }
      const startDate = new Date(startDateString);
      const startYear = startDate?.getUTCFullYear();
      const startMonth = padTo2Digits(startDate?.getUTCMonth() + 1); // TODO: Use moment lib (using startDate as string) instead of getting values from date
      const startDay = padTo2Digits(startDate?.getUTCDate());
      const formattedDate = `${startDay}/${startMonth}/${startYear}`;
      return startDate != null ? formattedDate : defaultTxt;
    },
  },
  658: {
    // 656 - [Development] Completion Date (17094)
    displayFn: (record: ReportRecord) => {
      const startDateString = getLatestDevelopmentDate(record);
      if (invalidDevelopmentDate(startDateString)) {
        return defaultTxt;
      }
      const startDate = new Date(startDateString);
      const completionYear = startDate?.getUTCFullYear();
      const completionQuarter = getQuarter(startDate);
      const formattedDate = `${completionYear} Q${completionQuarter}`;
      return startDate != null ? formattedDate : defaultTxt;
    },
  },
  659: {
    // 659 - [Development] Completion Year (17094)
    displayFn: (record: ReportRecord) => {
      const startDateString = getLatestDevelopmentDate(record);
      if (invalidDevelopmentDate(startDateString)) {
        return defaultTxt;
      }
      const startDate = new Date(startDateString);
      const completionYear = startDate.getFullYear() as unknown;
      return (completionYear as string) ?? defaultTxt;
    },
  },
  660: {
    // 660 - Developer
    displayFn: (record: ReportRecord) => {
      const fieldVal = getCompanyByRole(record, PROPERTY_COMPANY_ROLES.developer);
      const developerVal = fieldVal ?? defaultTxt;
      return Array.isArray(developerVal) ? concatValuesBy('/', ...developerVal) : developerVal;
    },
  },
  661: {
    // 661 - Funder
    displayFn: (record: ReportRecord) => {
      const fieldVal = getCompanyByRole(record, PROPERTY_COMPANY_ROLES.funder);
      const funderVal = fieldVal ?? defaultTxt;
      return Array.isArray(funderVal) ? concatValuesBy('/', ...funderVal) : funderVal;
    },
  },
  662: {
    // 662 - Architect
    displayFn: (record: ReportRecord) => {
      const fieldVal = getCompanyByRole(record, PROPERTY_COMPANY_ROLES.architect);
      const architectVal = fieldVal ?? defaultTxt;
      return Array.isArray(architectVal) ? concatValuesBy('/', ...architectVal) : architectVal;
    },
  },
  664: {
    // 664 Developer Comments
    displayFn: (record: ReportRecord, _) => {
      const latestDate = getLatestDevelopmentDate(record);
      const field = 'notes.notes';
      const fieldVal = getFieldByDate(latestDate, field, record);
      const devCommentsVal = fieldVal ?? defaultTxt;
      return Array.isArray(devCommentsVal) ? concatValuesBy(', ', ...devCommentsVal) : devCommentsVal;
    },
  },
  665: {
    // 662 - Owner Occupied
    displayFn: displayFnWrapper,
  },
  666: {
    // 662 - Development Website
    displayFn: displayFnWrapper,
  },
  667: {
    // 662 - Ref No
    displayFn: displayFnWrapper,
  },
};
